import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import {
  IFilterOrganizationParams,
  IOrganizarionsApiCall,
} from "utils/api.interfaces";
import { filterOrganizationParams } from "utils/common";

import axiosinterceptor from "../utils/api.interceptor";

const fetchOrganizations = (
  params: IFilterOrganizationParams
): Promise<IOrganizarionsApiCall> => {
  return axiosinterceptor
    .get(`/v1/Organizations`, {
      params: filterOrganizationParams(params),
    })
    .then<IOrganizarionsApiCall>((res) => res.data);
};
export default function useOrganizations(
  params: IFilterOrganizationParams,
  options?: Omit<
    UseQueryOptions<
      IOrganizarionsApiCall,
      Error,
      IOrganizarionsApiCall,
      ["organizations", IFilterOrganizationParams]
    >,
    "queryKey" | "queryFn"
  >
) {
  const queryOrganizations = useQuery({
    queryKey: ["organizations", params],
    queryFn: () => fetchOrganizations(params),
    enabled: !!params,
    ...options,
  });

  return {
    ...queryOrganizations,
    dataOrganizations: queryOrganizations.data,
    organizationsIsLoading: queryOrganizations.isLoading,
  };
}

/**
 * Adds an admin to an organization
 * @param organizationId - ID of the organization
 * @param adminId - ID of the user to add as admin
 * @returns Promise containing the result
 */
export const addAdminToOrganization = async (
  organizationId: string,
  adminId: string
): Promise<any> => {
  const res = await axiosinterceptor.post(`/v1/Organizations/admin`, {
    organizationId,
    adminId,
  });
  return res.data;
};

/**
 * Sets the owner of an organization
 * @param organizationId - ID of the organization
 * @param ownerId - ID of the user to set as owner
 * @returns Promise containing the result
 */
export const setOrganizationOwner = async (
  organizationId: string,
  ownerId: string
): Promise<any> => {
  const res = await axiosinterceptor.patch(`/v1/Organizations/owner`, {
    organizationId,
    ownerId,
  });
  return res.data;
};

/**
 * Removes an admin from an organization
 * @param organizationId - ID of the organization
 * @param adminId - ID of the admin to remove
 * @returns Promise containing the result
 */
export const removeAdminFromOrganization = async (
  organizationId: string,
  adminId: string
): Promise<any> => {
  const res = await axiosinterceptor.delete(`/v1/Organizations/admin`, {
    data: {
      organizationId,
      adminId,
    },
  });
  return res.data;
};
